/** You › Machines: machine cards (load, capacity, doctor) and branch/worktree hygiene. */
import { useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { COMMAND_PAYLOADS } from '@viko/protocol'
import { Button } from '@viko/transcript-ui'
import {
  Archive,
  ChevronRight,
  CircleAlert,
  CircleCheck,
  CircleX,
  GitBranch,
  GitMerge,
  Laptop,
  Monitor,
  Trash2,
} from 'lucide-react'
import { useState } from 'react'
import { MachineAccessNotice } from '../../components/MachineAccess.js'
import { MachineLoad } from '../../components/MachineLoad.js'
import { MachineResources } from '../../components/MachineResources.js' // machine-resources
import { Gauge } from '../../components/QuietMeter.js'
import { Ago, errorText, SkeletonRows, useToast } from '../../components/ui.js'
import { useBackend } from '../../lib/backend.js'
import { qk, useHygiene, useMachines, useProjects, useSchedMachines } from '../../lib/queries.js'
import type { DoctorCheck, HygieneItem, Machine } from '../../lib/types.js'
import { Section, ShowMore } from './shared.js'

export function MachinesSection() {
  return (
    <>
      <Machines />
      <Hygiene />
    </>
  )
}

function Machines() {
  const machines = useMachines()
  const sched = useSchedMachines()
  return (
    <Section id="machines" title="Machines" hideTitle>
      {machines.isPending ? <SkeletonRows n={2} /> : null}
      <div className="grid">
        {(machines.data ?? []).map((m) => (
          <MachineBlock
            key={m.id}
            m={m}
            asleep={!!sched.data?.find((x) => x.id === m.id)?.asleep}
          />
        ))}
      </div>
    </Section>
  )
}

/**
 * One machine (concept app-clarity `settings`): name + status word, one meta line, then meters as
 * rows (neutral; amber only when something waits), the screen gate, holding / waiting rows, and
 * `Disk: … free · n doctor checks to fix ▸` that opens the checks.
 */
function MachineBlock({ m, asleep }: { m: Machine; asleep: boolean }) {
  const b = useBackend()
  const toast = useToast()
  const fails = (m.doctor ?? []).filter((d) => d.status !== 'ok')
  const [doctorOpen, setDoctorOpen] = useState(false)
  const runDoctor = async () => {
    try {
      await b.dispatch('doctor.run', m.id, {})
      toast('Doctor is running; results update here')
    } catch (e) {
      toast(errorText(e), 'error')
    }
  }
  const status = m.online ? 'online' : asleep ? 'asleep' : 'offline'
  const reported = m.machineResources?.ts
  const caps = (m.capabilities ?? []).filter((c) => !c.startsWith('platform:'))
  const doctor = m.doctor?.length ? (
    <>
      {' · '}
      <button
        type="button"
        className={`st-inline-btn${fails.length ? ' st-wait' : ''}`}
        aria-expanded={doctorOpen}
        aria-controls={`doctor-${m.id}`}
        onClick={() => setDoctorOpen((v) => !v)}
      >
        {fails.length
          ? `${fails.length} doctor check${fails.length === 1 ? '' : 's'} to fix`
          : 'all checks pass'}
        <ChevronRight
          size={14}
          aria-hidden="true"
          className={`st-inline-btn__chev${doctorOpen ? ' is-open' : ''}`}
        />
      </button>
    </>
  ) : null
  return (
    <article className="st-machine" aria-label={m.name}>
      <header className="st-machine__head">
        <span className="st-machine__name">{m.name}</span>
        <span className={`st-machine__state is-${status}`}>
          <span className="st-dot" aria-hidden="true" />
          {status}
        </span>
        <span className="flex-1" />
        {reported ? (
          <span className="st-machine__seen">
            reported <Ago ts={reported} />
          </span>
        ) : null}
      </header>
      <p className="st-machine__meta">
        {m.platform === 'mac' ? (
          <Laptop size={16} aria-hidden="true" />
        ) : (
          <Monitor size={16} aria-hidden="true" />
        )}
        <span>
          {m.hardware ?? m.platform}
          {m.daemons[0]?.backend ? ` · key in ${m.daemons[0].backend.replace('-', ' ')}` : ''}
        </span>
      </p>
      <MachineAccessNotice machine={m} />
      {m.caps ? (
        <Gauge
          label="Runs"
          value={
            <>
              {m.caps.running} of {m.caps.maxRuns}
            </>
          }
          pct={(100 * m.caps.running) / m.caps.maxRuns}
          meterLabel={`${m.name} run capacity`}
          {...(m.online && m.caps.running < m.caps.maxRuns
            ? {
                sub: `${m.caps.maxRuns - m.caps.running} idle slot${m.caps.maxRuns - m.caps.running === 1 ? '' : 's'}`,
              }
            : {})}
        />
      ) : null}
      <MachineLoad machineId={m.id} name={m.name} />
      {m.machineResources ? (
        <MachineResources status={m.machineResources} name={m.name} diskTrailing={doctor} />
      ) : doctor ? (
        <p className="m-0 text-[14px] leading-5 text-text-2">Doctor{doctor}</p>
      ) : null}
      {doctorOpen && m.doctor?.length ? (
        <div id={`doctor-${m.id}`} className="grid gap-2">
          <ul className="m-0 grid list-none gap-2 p-0">
            {m.doctor.map((d) => (
              <DoctorRow key={d.id} d={d} machineId={m.id} online={m.online} />
            ))}
          </ul>
          {m.online ? (
            <div>
              <Button size="sm" variant="ghost" onClick={() => void runDoctor()}>
                Run doctor now
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}
      <p className="m-0 text-[14px] leading-5 text-text-2">
        <Link to="/settings/providers" hash="provider-accounts">
          Provider accounts
        </Link>
        {caps.length ? (
          <span className="text-text-3"> · {caps.slice(0, 8).join(' · ')}</span>
        ) : null}
      </p>
    </article>
  )
}

function DoctorRow({
  d,
  machineId,
  online,
}: {
  d: DoctorCheck
  machineId: string
  online: boolean
}) {
  const b = useBackend()
  const toast = useToast()
  const [busy, setBusy] = useState(false)
  const fix = async () => {
    if (!d.checkId) return
    setBusy(true)
    try {
      await b.dispatch('doctor.fix', machineId, { checkId: d.checkId })
      toast(`Fix sent: ${d.title}`)
    } catch (e) {
      toast(errorText(e), 'error')
    } finally {
      setBusy(false)
    }
  }
  const Icon = d.status === 'ok' ? CircleCheck : d.status === 'warn' ? CircleAlert : CircleX
  const tone =
    d.status === 'ok'
      ? 'text-status-ok'
      : d.status === 'warn'
        ? 'text-status-wait'
        : 'text-status-fail'
  return (
    <li className="grid grid-cols-[18px_1fr] gap-x-2 gap-y-1 text-[14px]">
      <Icon
        size={16}
        className={tone}
        aria-label={d.status === 'ok' ? 'Pass' : d.status === 'warn' ? 'Warning' : 'Fail'}
      />
      <span className="flex items-start gap-2">
        <span className="flex-1">{d.title}</span>
        {d.status !== 'ok' && d.checkId && online ? (
          <Button
            size="sm"
            variant="secondary"
            disabled={busy}
            onClick={() => void fix()}
            aria-label={`${d.title}: Fix`}
          >
            Fix
          </Button>
        ) : null}
      </span>
      {d.detail ? <span className="col-start-2 text-[14px] text-text-3">{d.detail}</span> : null}
      {d.fix ? <span className="col-start-2 text-[14px] text-text-2">Fix: {d.fix}</span> : null}
      {d.command ? (
        <code className="col-start-2 block rounded-md bg-code-bg px-2 py-1.5 font-mono text-[14px] break-all text-text-2">
          {d.command}
        </code>
      ) : null}
    </li>
  )
}

/**
 * Hygiene: unmerged branches, merged-but-unpruned branches and orphan worktrees across machines.
 * Land = `land.merge` (passkey), Archive = retire the workstream; Delete of unmerged work needs a
 * passkey and a `work.delete-unmerged` daemon command (enabled when the protocol has it).
 */
function Hygiene() {
  const h = useHygiene()
  const projects = useProjects()
  const b = useBackend()
  const qc = useQueryClient()
  const toast = useToast()
  const [done, setDone] = useState<Set<string>>(new Set())
  if (h.isError) return null
  const items = (h.data?.items ?? []).filter((i) => !done.has(i.id))
  const unmerged = items.filter((i) => i.kind === 'unmerged').length
  const wsFor = (i: HygieneItem) => {
    for (const p of projects.data ?? [])
      if (p.id === i.project || p.name === i.project || p.repo === i.project)
        for (const w of p.workstreams) if (w.branch === i.branch) return { p, w }
    for (const p of projects.data ?? [])
      for (const w of p.workstreams) if (w.branch === i.branch) return { p, w }
    return undefined
  }
  const deleteSupported = 'work.delete-unmerged' in COMMAND_PAYLOADS
  const act = async (i: HygieneItem, fn: () => Promise<unknown>, ok: string) => {
    try {
      await fn()
      setDone((d) => new Set([...d, i.id]))
      toast(ok)
    } catch (e) {
      toast(errorText(e), 'error')
    }
  }
  return (
    <Section id="hygiene" title="Branches & worktrees">
      <p className="m-0 px-4 pb-2 text-[14px] text-text-3">
        {h.data?.scannedAt ? (
          <>
            {unmerged} unmerged branches, {items.length - unmerged} to prune · scanned{' '}
            <Ago ts={h.data.scannedAt} />
          </>
        ) : (
          'No hygiene report yet — machines scan on a schedule.'
        )}
      </p>
      <ShowMore items={items} limit={5} label="branches">
        {(shown) => (
          <ul className="m-0 list-none p-0">
            {shown.map((i) => {
              const target = wsFor(i)
              return (
                <li key={i.id} className="row !cursor-default items-center">
                  <GitBranch
                    size={15}
                    className="mt-0.5 flex-none text-text-3"
                    aria-hidden="true"
                  />
                  <span className="grid min-w-0 flex-1 gap-0.5">
                    <code className="truncate font-mono text-[14px]">{i.branch}</code>
                    <span className="row__meta">
                      {target?.p.name ?? i.project} ·{' '}
                      {i.kind === 'unmerged'
                        ? `${i.ahead} ahead, ${i.behind} behind`
                        : i.kind === 'merged-unpruned'
                          ? 'merged, not pruned'
                          : 'orphan worktree'}
                      {i.bytes ? ` · ${(i.bytes / 1e9).toFixed(1)} GB` : ''} · {i.ageDays}d old
                    </span>
                  </span>
                  <span className="flex flex-none flex-wrap justify-end gap-1">
                    {i.kind === 'unmerged' && target && i.machineId ? (
                      <Button
                        size="sm"
                        variant="secondary"
                        icon={<GitMerge size={13} />}
                        onClick={() =>
                          void act(
                            i,
                            () =>
                              b.dispatch(
                                'land.merge',
                                i.machineId as string,
                                { workstreamId: target.w.id, branch: i.branch, target: 'main' },
                                { summary: `Land ${i.branch} into main` },
                              ),
                            'Landing started',
                          )
                        }
                      >
                        Land
                      </Button>
                    ) : null}
                    {target ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        icon={<Archive size={13} />}
                        onClick={() =>
                          void act(
                            i,
                            async () => {
                              await b.api.updateWorkstream(target.w.id, { status: 'archived' })
                              await qc.invalidateQueries({ queryKey: qk.projects })
                            },
                            'Archived',
                          )
                        }
                      >
                        Archive
                      </Button>
                    ) : null}
                    <Button
                      size="sm"
                      variant="ghost"
                      icon={<Trash2 size={13} />}
                      disabled={!deleteSupported || !i.machineId}
                      title={
                        deleteSupported
                          ? i.kind === 'unmerged'
                            ? 'Deleting unmerged work needs a passkey'
                            : undefined
                          : 'viko has no prune command yet'
                      }
                      onClick={() =>
                        void act(
                          i,
                          () =>
                            b.dispatch(
                              'work.delete-unmerged' as never,
                              i.machineId as string,
                              { branch: i.branch, kind: i.kind } as never,
                              { summary: `Delete ${i.branch}` },
                            ),
                          'Deleted',
                        )
                      }
                    >
                      Delete
                    </Button>
                  </span>
                </li>
              )
            })}
          </ul>
        )}
      </ShowMore>
    </Section>
  )
}
